import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import Form from "../../common_nextgen/common_components/form/Form";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import {
  PRICE_SCHEDULE_MODES,
  ROUTE_PATHS,
} from "../../common_nextgen/utils/Constants";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";
import { formFields } from "./MaxCopayScheduleFormConfig";

const screenInfo = {
  ADD: ["Add Max Copay Schedule Detail", "RCMCY002"],
  CHANGE: ["Max Copay Schedule Detail", "RCMCY003"],
  VIEW: ["Max Copay Schedule Detail", "RCMCY003"],
};

const MaxCopayScheduleForm = ({ mode }) => {
  const location = useLocation();
  const { axios_helper_no_header } = useAxios();

  const [stateVal, setStateVal] = useState(location.state ?? []);

  const [currentState = {}] = [...stateVal].reverse();
  const previousState = [...stateVal];
  previousState.splice(stateVal.length - 1, 1);
  const {
    selectedSchedules = [],
    previousScreen = ROUTE_PATHS.MAX_COPAY_SCHEDULE,
    currentIndex: initialIndex = 0,
    externalMode: initialExternalMode = false,
  } = currentState;

  const [externalMode, setExternalMode] = useState(initialExternalMode);
  const navigateToPage = useCustomNavigate();
  const [selectedOption, setSelectedOption] = useState("no");
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentMode, setCurrentMode] = useState(mode);
  const [initialFormData, setInitialFormData] = useState({});
  const [dataForSubmission, setDataForSubmission] = useState({});
  const [constraintErrors, setConstraintErrors] = useState([]);
  const toast = useToast();

  const handleBack = () => {
    if (externalMode) {
      const message = {
        message: "navigate-to-external-program",
        url: previousScreen,
        parameters: {
          state: [...previousState],
        },
      };
      console.log("back messages", message);
      window.parent.postMessage(message, "*");
    } else if (currentIndex < selectedSchedules.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentMode(mode);
    } else {
      navigateToPage(previousScreen, previousState);
    }
  };

  const getPageTitle = () => {
    const [screenTitle, screenNumber] = isAddMode(mode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  };

  const submitFormData = (data) => {
    setDataForSubmission(data);
  };

  const submitForm = async (dataForSubmission) => {
    let url = "";
    let verb = "";
    let screenNumber = "";

    if (isAddMode(currentMode)) {
      url = "api/mcyMaxCopaySchedule";
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/mcyMaxCopaySchedule";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];

      if (
        JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData)
      ) {
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "No Change of Information",
          life: 3000,
        });
        return;
      }
    }

    const data = { ...dataForSubmission, screenNumber };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null
      );
      setInitialFormData(responseData);
      toast.current.show({
        severity: "success",
        summary: "",
        detail: responseStatus.message,
        life: 3000,
      });
      if (isAddMode(currentMode)) {
        const [previousLastRecord = {}] = previousState.splice(
          previousState.length - 1,
          1
        );
        const { paramsData = {} } = previousLastRecord;
        const updatedParamsData = {
          ...paramsData,
          mcyMaxCopaySchedule: responseData["mcyMaxCopaySchedule"],
        };
        const updatedPreviousState = [
          ...previousState,
          { ...previousLastRecord, paramsData: updatedParamsData },
        ];
        const updatedCurrentState = {
          ...currentState,
          selectedSchedules: [{ ...responseData }],
          currentIndex: 0,
          previousScreen: previousScreen,
        };
        navigateToPage(ROUTE_PATHS.EDIT_MAX_COPAY_SCHEDULE, [
          ...updatedPreviousState,
          { ...updatedCurrentState },
        ]);
      }
    } catch (error) {
      const { responseStatus: { displayErrorFields = null, message } = {} } =
        error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};

    if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.chmod = "View";
      keyFunc.F7 = () => {
        navigateToPage(ROUTE_PATHS.MAX_COPAY_SCHEDULE_STEPS, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedSchedules: [initialFormData],
            previousScreen: location.pathname,
            type: "1",
          },
        ]);
      };
      keyName.F7 = "Max Copay Steps";
      keyFunc.F9 = () => {
        navigateToPage(ROUTE_PATHS.MAX_COPAY_SCHEDULE_STEPS, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedSchedules: [initialFormData],
            previousScreen: location.pathname,
            type: "3",
          },
        ]);
      };
      keyName.F9 = "Max Copay+Diff Steps";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      keyName.chmod = "Edit";
      keyFunc.F7 = () =>
        navigateToPage(ROUTE_PATHS.MAX_COPAY_SCHEDULE_STEPS, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedSchedules: [initialFormData],
            previousScreen: location.pathname,
            type: "1",
          },
        ]);
      keyName.F7 = "Max Copay Steps";
      keyFunc.F9 = () =>
        navigateToPage(ROUTE_PATHS.MAX_COPAY_SCHEDULE_STEPS, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedSchedules: [initialFormData],
            previousScreen: location.pathname,
            type: "3",
          },
        ]);
      keyName.F9 = "Max Copay+Diff Steps";
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex, initialFormData, externalMode, previousState, currentState]);

  useEffect(() => {
    const fetchInitialData = async () => {
      let url = isAddMode(currentMode)
        ? `api/mcyMaxCopaySchedule/initialFormData/${screenInfo.ADD[1]}`
        : `api/mcyMaxCopaySchedule/${encodeURIComponent(
            selectedSchedules[currentIndex]?.mcyMaxCopaySchedule
          )}`;

      const { responseData } = await axios_helper_no_header("GET", url);
      setInitialFormData(responseData);
    };

    if (
      !isAddMode(currentMode) &&
      !selectedSchedules[currentIndex]?.mcyMaxCopaySchedule
    ) return;
      fetchInitialData();
  }, [currentMode, currentIndex, externalMode, selectedSchedules[currentIndex]]);

  useEffect(() => {
      const handleMessage = (event) => {
        const { type, data } = event.data;
        if (type === "set-params") {
          console.log("handleMessage", data);
          setStateVal(data);
          setExternalMode(true);
        }
      };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (location.state) {
      setStateVal(location.state);
    }
  }, [location.state]);

  useEffect(() => {
    setCurrentMode(mode);
  }, [mode]);

  return (
    <div className="grid container">
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
        />
      </div>
    </div>
  );
};

export default MaxCopayScheduleForm;
